import { Box, Typography } from '@mui/material';
import DeliveryEvolutionTooltip from 'client/modules/Analyze/components/DeliveryEvolutionTooltip';
import {
  coerceDeliveryInterval,
  estimateDeliveryRangeDays,
  getDeliveryIntervalOptions,
} from 'client/modules/Analyze/deliveryTimeSeriesInterval';
import parseDeliveryPeriod from 'client/modules/Analyze/parseDeliveryPeriod';
import ActivityIndicator from 'client/modules/App/components/ActivityIndicator/ActivityIndicator';
import AutoWidth from 'client/modules/Surveys/components/AutoWidth/AutoWidth';
import LineTooltip from 'client/modules/Surveys/components/LineTooltip/LineTooltip';
import { useGetDeliveryTimeSeriesQuery } from 'client/modules/Surveys/surveyDeliveryApiSlice';
import {
  BLUE,
  cardSx,
  INK,
  PURPLE,
  PROMOTER,
  PASSIVE,
  SECONDARY,
  sectionTitleSx,
} from 'client/modules/Analyze/analyzeTheme';
import styles from 'client/modules/Surveys/pages/DashboardPage/DashboardPage.css';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

const STAGE_COLORS = [BLUE, PURPLE, PROMOTER, PASSIVE, '#455a64'];

const deliveryActionMessages = defineMessages({
  shown: { id: 'Person.status.shown', defaultMessage: 'Survey shown' },
  responded: { id: 'Person.status.responded', defaultMessage: 'Survey responded' },
  commented: { id: 'Person.status.commented', defaultMessage: 'Survey commented' },
  sent: { id: 'Person.status.sent', defaultMessage: 'Survey sent' },
  opened: { id: 'Person.status.opened', defaultMessage: 'Survey opened' },
  scheduled: { id: 'Person.status.scheduled', defaultMessage: 'Survey scheduled' },
});

const messages = defineMessages({
  title: {
    id: 'analyze.delivery.evolutionTitle',
    defaultMessage: 'Funnel over time',
  },
  daily: { id: 'Dashboard.daily', defaultMessage: 'Daily' },
  weekly: { id: 'Dashboard.weekly', defaultMessage: 'Weekly' },
  monthly: { id: 'Dashboard.monthly', defaultMessage: 'Monthly' },
  loadError: {
    id: 'analyze.delivery.timeSeriesLoadError',
    defaultMessage: 'Failed to load funnel history',
  },
  noData: {
    id: 'analyze.delivery.timeSeriesNoData',
    defaultMessage: 'No delivery activity in this period',
  },
});

const MIN_CHART_WIDTH = 200;

function useStageLabel(stage) {
  const { formatMessage } = useIntl();
  const msg = deliveryActionMessages[stage?.action];
  return msg ? formatMessage(msg) : stage?.label || stage?.action;
}

// Legend matching the Over time chart: a colored line indicator + label.
// Each item stays clickable to toggle its series (dimmed when hidden).
function DeliverySeriesLegend({ funnelStages, visibleSeries, onToggle }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2.5, mb: 1.5 }}>
      {funnelStages.map((stage, index) => (
        <SeriesLegendItem
          key={stage.action}
          stage={stage}
          color={STAGE_COLORS[index % STAGE_COLORS.length]}
          visible={visibleSeries.has(stage.action)}
          onToggle={() => onToggle(stage.action)}
        />
      ))}
    </Box>
  );
}

function SeriesLegendItem({ stage, color, visible, onToggle }) {
  const label = useStageLabel(stage);
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={visible}
      onClick={onToggle}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onToggle()}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.9,
        cursor: 'pointer',
        userSelect: 'none',
        opacity: visible ? 1 : 0.4,
        transition: 'opacity .2s',
      }}
    >
      <Box sx={{ width: 16, height: 3, borderRadius: '2px', bgcolor: color }} />
      <Typography sx={{ fontSize: 13, color: SECONDARY, fontWeight: 500 }}>{label}</Typography>
    </Box>
  );
}

SeriesLegendItem.propTypes = {
  stage: PropTypes.shape({
    action: PropTypes.string.isRequired,
    label: PropTypes.string,
  }).isRequired,
  color: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

DeliverySeriesLegend.propTypes = {
  funnelStages: PropTypes.arrayOf(PropTypes.object).isRequired,
  visibleSeries: PropTypes.instanceOf(Set).isRequired,
  onToggle: PropTypes.func.isRequired,
};

const INTERVAL_OPTIONS = [
  { value: 'day', key: 'daily' },
  { value: 'week', key: 'weekly' },
  { value: 'month', key: 'monthly' },
];

// Segmented control for the chart interval, per the design (gray track,
// active option lifted on a white pill).
function IntervalSegmentedControl({ value, allowed, onChange }) {
  const { formatMessage } = useIntl();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: '#F1F4F8',
        borderRadius: '8px',
        p: '3px',
      }}
    >
      {INTERVAL_OPTIONS.map(opt => {
        const disabled = opt.value !== 'month' && !allowed.includes(opt.value);
        const active = opt.value === value;
        return (
          <Box
            key={opt.value}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-pressed={active}
            aria-disabled={disabled}
            onClick={() => !disabled && onChange(opt.value)}
            onKeyDown={e =>
              !disabled && (e.key === 'Enter' || e.key === ' ') && onChange(opt.value)
            }
            sx={{
              px: 1.75,
              py: 0.75,
              borderRadius: '6px',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '.4px',
              textTransform: 'uppercase',
              cursor: disabled ? 'not-allowed' : 'pointer',
              userSelect: 'none',
              color: active ? INK : SECONDARY,
              bgcolor: active ? '#fff' : 'transparent',
              boxShadow: active ? '0 1px 2px rgba(16,24,40,.1)' : 'none',
              opacity: disabled ? 0.4 : 1,
              transition: 'color .15s, background-color .15s',
              '&:hover': disabled || active ? undefined : { color: INK },
            }}
          >
            {formatMessage(messages[opt.key])}
          </Box>
        );
      })}
    </Box>
  );
}

IntervalSegmentedControl.propTypes = {
  value: PropTypes.string.isRequired,
  allowed: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

function DeliveryLineChart({ width, chartData, interval, chartSeries, funnelStages }) {
  if (!width || width < MIN_CHART_WIDTH || chartData.length === 0) {
    return null;
  }

  return (
    <LineTooltip
      chartPeriod={interval}
      surveyType="delivery"
      svgClassName={styles.chart}
      data={chartData}
      height={320}
      width={width}
      margins={{ left: 50, right: 0, top: 10, bottom: 30 }}
      chartSeries={chartSeries}
      x={d => d?.key}
      y={d => +d}
      point={d => d}
      xScale="time"
      yScale="linear"
      animateChart={false}
      showTooltipLine
    >
      <DeliveryEvolutionTooltip
        chartPeriod={interval}
        chartSeries={chartSeries}
        funnelStages={funnelStages}
      />
    </LineTooltip>
  );
}

DeliveryLineChart.propTypes = {
  width: PropTypes.number,
  chartData: PropTypes.arrayOf(PropTypes.object).isRequired,
  interval: PropTypes.string.isRequired,
  chartSeries: PropTypes.arrayOf(PropTypes.object).isRequired,
  funnelStages: PropTypes.arrayOf(PropTypes.object),
};

function DeliveryEvolutionChart({ surveyId, channel, audienceParams }) {
  const { formatMessage } = useIntl();
  const { since, until } = audienceParams;
  const rangeDays = useMemo(() => estimateDeliveryRangeDays(since, until), [since, until]);
  const { allowed, defaultInterval } = useMemo(
    () => getDeliveryIntervalOptions(rangeDays),
    [rangeDays],
  );
  const [interval, setInterval] = useState(defaultInterval);
  const [visibleSeries, setVisibleSeries] = useState(() => new Set());

  useEffect(() => {
    setInterval(prev => coerceDeliveryInterval(prev, rangeDays));
  }, [rangeDays]);

  const params = useMemo(
    () => ({
      survey: surveyId,
      channel,
      interval,
      ...audienceParams,
    }),
    [surveyId, channel, interval, audienceParams],
  );

  const { data, isFetching, isError, isSuccess } = useGetDeliveryTimeSeriesQuery(params, {
    skip: !surveyId || !channel,
  });

  const funnelStages = data?.funnelStages;

  useEffect(() => {
    if (funnelStages?.length) {
      setVisibleSeries(new Set(funnelStages.map(stage => stage.action)));
    }
  }, [surveyId, channel, funnelStages]);

  const toggleSeries = useCallback(action => {
    setVisibleSeries(prev => {
      if (prev.has(action) && prev.size <= 1) {
        return prev;
      }
      const next = new Set(prev);
      if (next.has(action)) {
        next.delete(action);
      } else {
        next.add(action);
      }
      return next;
    });
  }, []);

  const seriesInterval = data?.interval;
  const dataReady = isSuccess && seriesInterval === interval;

  const chartData = useMemo(() => {
    if (!dataReady || !data?.periods?.length) {
      return [];
    }
    const parseAs = seriesInterval || interval;
    return data.periods
      .map(row => {
        const key = parseDeliveryPeriod(row.period, parseAs);
        if (!key) {
          return null;
        }
        return { key, ...row.metrics };
      })
      .filter(Boolean)
      .sort((a, b) => a.key - b.key);
  }, [data?.periods, dataReady, interval, seriesInterval]);

  const chartSeries = useMemo(
    () =>
      dataReady
        ? (funnelStages || []).map((stage, index) => ({
            field: stage.action,
            symbol: 'circle',
            symbolSize: 4,
            strokeWidth: 2,
            color: STAGE_COLORS[index % STAGE_COLORS.length],
            hideLabels: index > 0,
          }))
        : [],
    [funnelStages, dataReady],
  );

  const visibleChartSeries = useMemo(() => {
    const visible = chartSeries.filter(series => visibleSeries.has(series.field));
    const showAllLabels = !visibleSeries.has('shown') && visible.length > 0;
    return visible.map((series, index) => ({
      ...series,
      hideLabels: showAllLabels ? false : index > 0,
    }));
  }, [chartSeries, visibleSeries]);

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={sectionTitleSx}>{formatMessage(messages.title)}</Typography>
        <IntervalSegmentedControl value={interval} allowed={allowed} onChange={setInterval} />
      </Box>
      {isFetching && !dataReady && <ActivityIndicator inline />}
      {isError && !data && (
        <Typography color="error" variant="body2">
          {formatMessage(messages.loadError)}
        </Typography>
      )}
      {dataReady && chartData.length === 0 && !isFetching && (
        <Typography variant="body2" color="text.secondary">
          {formatMessage(messages.noData)}
        </Typography>
      )}
      {dataReady && chartData.length > 0 && visibleChartSeries.length > 0 && (
        <>
          <DeliverySeriesLegend
            funnelStages={funnelStages}
            visibleSeries={visibleSeries}
            onToggle={toggleSeries}
          />
          <AutoWidth>
            <DeliveryLineChart
              chartData={chartData}
              interval={interval}
              chartSeries={visibleChartSeries}
              funnelStages={funnelStages}
            />
          </AutoWidth>
        </>
      )}
    </Box>
  );
}

DeliveryEvolutionChart.propTypes = {
  surveyId: PropTypes.string.isRequired,
  channel: PropTypes.string.isRequired,
  audienceParams: PropTypes.object.isRequired,
};

export default DeliveryEvolutionChart;
